import React, { useEffect } from 'react';
import {t} from "../../../i18n";

export interface LocationOrderScanData {
    locationNo?: string | null;
    trackingNumber?: string | null;
    locationOrderNo?: number | null;
    pickingActionDetail?: string | null;
}

interface LocationOrderScanOverlayProps {
    visible: boolean;
    data: LocationOrderScanData | null | undefined;
    onClose: () => void;
    color?: 'green' | 'purple' | 'blue' | 'red' | 'orange';
}

const getColorConfig = (color: string = 'green') => {
    const configs: Record<string, { textColor: string; bgColor: string }> = {
        green: {
            textColor: 'text-green-700',
            bgColor: 'bg-green-200 bg-opacity-100',
        },
        purple: {
            textColor: 'text-purple-700',
            bgColor: 'bg-purple-200 bg-opacity-100',
        },
        blue: {
            textColor: 'text-blue-700',
            bgColor: 'bg-blue-200 bg-opacity-100',
        },
        red: {
            textColor: 'text-red-700',
            bgColor: 'bg-red-200 bg-opacity-100',
        },
        orange: {
            textColor: 'text-orange-700',
            bgColor: 'bg-orange-200 bg-opacity-100',
        },
    };
    
    return configs[color] || configs.green;
};

// 简单的键盘关闭函数
const closeKeyboard = () => {
    // 移除当前焦点
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
    }
    
    // 失焦所有输入框
    const inputs = document.querySelectorAll('input, textarea');
    inputs.forEach(input => {
        if (input instanceof HTMLElement) {
            input.blur();
        }
    });
};

export const LocationOrderScanOverlay: React.FC<LocationOrderScanOverlayProps> = ({
    visible,
    data,
    onClose,
    color = 'green',
}) => {
    const config = getColorConfig(color);

    // 当overlay显示时关闭键盘
    useEffect(() => {
        if (visible) {
            closeKeyboard();
        }
    }, [visible]);

    if (!visible || !data) {
        return null;
    }

    // 如果没有任何数据，不展示
    if (!data.locationNo && !data.trackingNumber && !data.locationOrderNo && !data.pickingActionDetail) {
        return null;
    }

    // 根据下架指令细分获取显示文案和提示信息
    const getActionInfo = (pickingActionDetail?: string | null) => {
        switch (pickingActionDetail) {
            case 'ReDeliver':
                return {
                    title: t('pda.pages.picking.redispatch'),
                    message: t('pda.pages.picking.redispatchMessage')
                };
            case 'RePrintReDeliver':
                return {
                    title: t('pda.pages.picking.rePrintRedispatch'),
                    message: t('pda.pages.picking.rePrintRedispatchMessage')
                };
            case 'ReturnToMerchant':
                return {
                    title: t('pda.pages.picking.returnToMerchant'),
                    message: t('pda.pages.picking.returnToMerchantMessage')
                };
            case 'ReturnToHubDestroy':
                return {
                    title: t('pda.pages.picking.returnToHubDestroy'),
                    message: t('pda.pages.picking.returnToHubDestroyMessage')
                };
            case 'Return':
                return {
                    title: t('pda.pages.picking.return'),
                    message: t('pda.pages.picking.returnMessage')
                };
            default:
                return null;
        }
    };

    const actionInfo = getActionInfo(data.pickingActionDetail);

    return (
        <div 
            className={`fixed inset-0 w-full h-full flex flex-col items-center gap-3 justify-center ${config.bgColor}`}
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 99999
            }}
        >
            {/* 显示nextAction对应的标题及提示消息（重派/退回） */}
            {actionInfo && (
                <>
                    <div
                        className={`font-bold ${config.textColor} text-center mb-4`}
                        style={{
                            fontSize: 'min(15vw, 3.75rem)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {actionInfo.title}
                    </div>
                    <div
                        className={`mt-8 font-bold ${config.textColor} text-center px-4`}
                        style={{
                            fontSize: 'min(5vw, 1.25rem)',
                            lineHeight: '1.5',
                            maxWidth: '90vw',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {actionInfo.message}
                    </div>
                </>
            )}
            {data.locationNo && (
                <div
                    className={`font-bold ${config.textColor} text-center px-2`}
                    style={{
                        fontSize: data.locationNo.length > 15 ? '1.5rem' : '1.875rem',
                        lineHeight: '1.3',
                        maxWidth: '95vw'
                    }}
                >
                    {data.locationNo}
                </div>
            )}
            {data.trackingNumber && (
                <div
                    className={`text-xl mt-5 font-bold text-center px-2`}
                    style={{
                        wordBreak: 'break-all',
                        maxWidth: '95vw',
                        lineHeight: '1.2'
                    }}
                >
                    {data.trackingNumber}
                </div>
            )}
            {data.locationOrderNo !== null && data.locationOrderNo !== undefined && (
                <div 
                    className={`text-4xl mt-5 font-bold text-center break-all px-1`}
                >
                    {t('pda.common.no')} {String(data.locationOrderNo).padStart(4, '0')}
                </div>
            )}
        </div>
    );
};
